import React, { useState, useContext, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Avatar,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Chat as ChatIcon,
  History as HistoryIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc, 
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import { Firestore, CloudFunctions, FirebaseStorage } from '../FirebaseAuth/firebase';
import { AuthContext } from '../FirebaseAuth';
import CardWithHeader from '../Common/CardWithHeader';
import { 
  getLocalStorage, 
  LOCAL_STORAGE_CURRENT_USER, 
  LOCAL_STORAGE_CURRENT_ORG,
  LOCAL_STORAGE_CURRENT_ACCOUNT 
} from '../../utils/localStorage';

/**
 * Format AI response text with markdown-like styling using Material-UI components
 * @param {string} text - The raw text response from AI
 * @returns {JSX.Element} - Formatted React component
 */
const formatAIResponse = (text) => {
  if (!text) return null;

  // Split text into lines for processing
  const lines = text.split('\n');
  const elements = [];
  let currentListItems = [];
  let inCodeBlock = false;
  let codeBlockContent = [];

  const flushListItems = () => {
    if (currentListItems.length > 0) {
      elements.push(
        <Box component="ul" key={`list-${elements.length}`} sx={{ my: 1, pl: 2 }}>
          {currentListItems.map((item, index) => (
            <Typography component="li" key={index} variant="body2" sx={{ mb: 0.5 }}>
              {formatInlineText(item)}
            </Typography>
          ))}
        </Box>
      );
      currentListItems = [];
    }
  };

  const formatInlineText = (text) => {
    if (!text) return text;
    
    // Handle bold text **text**
    let formatted = text.replace(/\*\*(.*?)\*\*/g, (match, content) => `<strong>${content}</strong>`);
    
    // Handle italic text *text*
    formatted = formatted.replace(/(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/g, (match, content) => `<em>${content}</em>`);
    
    // Handle inline code `code`
    formatted = formatted.replace(/`([^`]+)`/g, (match, content) => `<code style="background-color: #f5f5f5; padding: 2px 4px; border-radius: 3px; font-family: monospace;">${content}</code>`);
    
    return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Handle code blocks
    if (trimmedLine.startsWith('```')) {
      if (inCodeBlock) {
        // End code block
        elements.push(
          <Paper
            key={`codeblock-${elements.length}`}
            elevation={0}
            sx={{
              p: 2,
              my: 1,
              bgcolor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              overflow: 'auto'
            }}
          >
            {codeBlockContent.join('\n')}
          </Paper>
        );
        codeBlockContent = [];
        inCodeBlock = false;
      } else {
        // Start code block
        flushListItems();
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      return;
    }

    // Handle headers (# ## ###)
    if (trimmedLine.startsWith('#')) {
      flushListItems();
      const headerLevel = trimmedLine.match(/^#+/)[0].length;
      const headerText = trimmedLine.replace(/^#+\s*/, '');
      const variant = headerLevel === 1 ? 'h5' : headerLevel === 2 ? 'h6' : 'subtitle1';
      
      elements.push(
        <Typography 
          key={`header-${elements.length}`} 
          variant={variant} 
          sx={{ 
            mt: elements.length > 0 ? 2 : 0, 
            mb: 1, 
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          {formatInlineText(headerText)}
        </Typography>
      );
      return;
    }

    // Handle list items (- or *)
    if (/^[-*]\s/.test(trimmedLine)) {
      const listItemText = trimmedLine.replace(/^[-*]\s/, '');
      currentListItems.push(listItemText);
      return;
    }

    // Handle numbered lists (1. 2. etc)
    if (/^\d+\.\s/.test(trimmedLine)) {
      if (currentListItems.length === 0) {
        // Start new numbered list
      }
      const listItemText = trimmedLine.replace(/^\d+\.\s/, '');
      currentListItems.push(listItemText);
      return;
    }

    // Handle regular paragraphs
    if (trimmedLine) {
      flushListItems();
      elements.push(
        <Typography 
          key={`para-${elements.length}`} 
          variant="body2" 
          sx={{ mb: 1, lineHeight: 1.6 }}
        >
          {formatInlineText(trimmedLine)}
        </Typography>
      );
    } else if (elements.length > 0) {
      // Add spacing for empty lines between content
      flushListItems();
      elements.push(
        <Box key={`space-${elements.length}`} sx={{ height: 8 }} />
      );
    }
  });

  // Flush any remaining list items
  flushListItems();

  return <Box>{elements}</Box>;
};

/**
 * AI Assistant Component - provides contextual AI chat for organizations, collections, and sites
 * 
 * @param {Object} props - Component props
 * @param {String} props.contextType - 'organization', 'account', or 'site'
 * @param {Object} props.contextData - The organization, account, or site data object
 * @param {String} props.accountId - Account ID (required for site context)
 * @param {String} props.siteId - Site ID (required for site context)
 * @param {String} props.title - Optional custom title for the AI Assistant card
 * @param {Number} props.maxHeight - Maximum height for the conversation area
 */
const AIAssistant = ({
  contextType,
  contextData,
  accountId,
  siteId,
  title = "AI Assistant",
  maxHeight = 400
}) => {
  const { authUser } = useContext(AuthContext);
  const currentUser = getLocalStorage(LOCAL_STORAGE_CURRENT_USER);
  const currentOrg = getLocalStorage(LOCAL_STORAGE_CURRENT_ORG);
  const currentAccount = getLocalStorage(LOCAL_STORAGE_CURRENT_ACCOUNT);

  const [conversations, setConversations] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedConversations, setExpandedConversations] = useState(new Set());
  const [cachedContext, setCachedContext] = useState(null);
  const [contextCacheTime, setContextCacheTime] = useState(null);

  // Determine the Firestore collection path based on context
  const getCollectionPath = useCallback(() => {
    switch (contextType) {
      case 'organization':
        return `organizations/${contextData?.id || currentOrg?.id}/aiConversations`;
      case 'account':
        return `accounts/${contextData?.id || currentAccount?.id}/aiConversations`;
      case 'site':
        return `accounts/${accountId || currentAccount?.id}/sites/${siteId}/aiConversations`;
      default:
        throw new Error('Invalid context type');
    }
  }, [contextType, contextData, currentOrg, currentAccount, accountId, siteId]);

  // Check if user can delete a conversation
  const canDeleteConversation = (conversation) => {
    if (!authUser?.user?.uid || !conversation) return false;
    
    // Creator can delete
    if (conversation.userId === authUser.user.uid) return true;
    
    // kwallAdmin can delete
    if (currentUser?.role?.includes('kwallAdmin')) return true;
    
    // Organization/Account admins can delete
    if (contextType === 'organization' && currentOrg?.admins?.includes(authUser.user.uid)) return true;
    if ((contextType === 'account' || contextType === 'site') && currentAccount?.admins?.includes(authUser.user.uid)) return true;
    
    return false;
  };

  // Load latest conversation for Chat tab only 
  const loadLatestConversation = useCallback(async () => {
    if (!contextData && !currentOrg && !currentAccount) {
      console.log('⚠️ AI Assistant: No context data available, skipping conversation load');
      return;
    }

    try {
      const collectionPath = getCollectionPath();
      const conversationsRef = collection(Firestore, collectionPath);
      const q = query(conversationsRef, orderBy('createdAt', 'desc'), limit(1));
      
      const snapshot = await getDocs(q);
      const conversationData = [];
      snapshot.forEach((doc) => {
        conversationData.push({ id: doc.id, ...doc.data() });
      });
      
      setConversations(conversationData);
    } catch (error) {
      console.error('❌ AI Assistant: Error loading latest conversation:', error);
      setError('Failed to load conversation');
    }
  }, [contextData, currentAccount, currentOrg, getCollectionPath]);

  // Load all conversations for History tab
  const loadAllConversations = useCallback(async () => {
    if (!contextData && !currentOrg && !currentAccount) {
      return;
    }

    try {
      const collectionPath = getCollectionPath();
      const conversationsRef = collection(Firestore, collectionPath);
      const q = query(conversationsRef, orderBy('createdAt', 'asc'), limit(50));
      
      const snapshot = await getDocs(q);
      const conversationData = [];
      snapshot.forEach((doc) => {
        conversationData.push({ id: doc.id, ...doc.data() });
      });
      
      setConversations(conversationData);
    } catch (error) {
      console.error('❌ AI Assistant: Error loading conversation history:', error);
      setError('Failed to load conversation history');
    }
  }, [contextData, currentAccount, currentOrg, getCollectionPath]);

  // Load conversations based on active tab
  useEffect(() => {
    if (tabValue === 0) {
      // Chat tab - load latest conversation only
      loadLatestConversation();
    } else if (tabValue === 1) {
      // History tab - always reload to ensure we have all conversations
      loadAllConversations();
    }
  }, [tabValue, loadLatestConversation, loadAllConversations]);

  // Prepare optimized context data using pre-computed AI summaries
  const prepareContextData = useCallback(async () => {
    const context = {
      type: contextType,
      timestamp: new Date().toISOString()
    };

    try {
      switch (contextType) {
        case 'organization':
          // Fetch pre-computed organization AI summary
          const orgData = contextData || currentOrg;
          const orgId = orgData?.id;
          
          if (!orgId) {
            console.warn('No organization ID available');
            break;
          }
          
          console.log('Fetching organization profile and structured data for:', orgId);
          
          try {
            // Fetch structured organization profile
            const orgProfileDoc = await getDoc(doc(Firestore, `organizations/${orgId}/orgProfile/current`));
            
            if (orgProfileDoc.exists()) {
              const orgProfile = orgProfileDoc.data();
              console.log('✅ Organization Profile found!', orgProfile);
              
              context.organization = {
                id: orgId,
                name: orgData?.name,
                description: orgData?.description,
                accountType: orgData?.accountType,
                primaryUrl: orgData?.primaryUrl,
                
                // Use structured profile data
                overview: orgProfile.overview,
                distribution: orgProfile.distribution,
                performance: orgProfile.performance,
                recommendations: orgProfile.recommendations,
                keyInsights: orgProfile.keyInsights,
                diffbotProfile: orgProfile.diffbotProfile,
                allSiteUrls: orgProfile.allSiteUrls, // Include all site URLs for AI context
                
                // Legacy compatibility
                totalAccounts: orgProfile.overview?.totalAccounts || 0,
                totalSites: orgProfile.overview?.totalSites || 0,
                totalDomains: orgProfile.overview?.totalDomains || 0,
                lastAnalyzed: orgProfile.lastAnalyzed
              };
              
              console.log('Loaded organization profile:', {
                totalAccounts: context.organization.totalAccounts,
                totalSites: context.organization.totalSites,
                totalDomains: context.organization.totalDomains,
                hasDiffbotData: !!orgProfile.diffbotProfile
              });
            } else {
              // Fallback to basic organization data if no AI summary exists
              console.warn('No AI summary found for organization, using basic data');
              context.organization = {
                id: orgId,
                name: orgData?.name,
                description: orgData?.description,
                accountType: orgData?.accountType,
                primaryUrl: orgData?.primaryUrl,
                totalAccounts: orgData?.accounts?.length || 0,
                totalSites: 0,
                aiSummary: "AI summary not yet generated for this organization.",
                needsSummaryGeneration: true
              };
            }
          } catch (error) {
            console.error('Error fetching organization AI summary:', error);
            // Fallback to basic data
            context.organization = {
              id: orgId,
              name: orgData?.name,
              description: orgData?.description,
              accountType: orgData?.accountType,
              totalAccounts: orgData?.accounts?.length || 0,
              error: "Could not load AI summary"
            };
          }
          break;
          
        case 'account':
          // Fetch pre-computed account AI summary
          const accountData = contextData || currentAccount;
          const currentAccountId = accountData?.id;
          
          if (!currentAccountId) {
            console.warn('No account ID available');
            break;
          }
          
          console.log('Fetching account structured summary for:', currentAccountId);
          
          try {
            const accountSummaryDoc = await getDoc(doc(Firestore, `accounts/${currentAccountId}/structuredSummary/current`));
            
            if (accountSummaryDoc.exists()) {
              const accountSummary = accountSummaryDoc.data();
              
              context.account = {
                id: currentAccountId,
                name: accountData?.name,
                accountType: accountData?.accountType,
                description: accountData?.description,
                status: accountData?.status,
                
                // Use structured summary data
                overview: accountSummary.overview,
                performance: accountSummary.performance,
                recommendations: accountSummary.recommendations,
                sites: accountSummary.sites,
                
                // Legacy compatibility
                totalSites: accountSummary.overview?.totalSites || 0,
                averagePerformance: accountSummary.overview?.averagePerformance,
                lastAnalyzed: accountSummary.lastAnalyzed
              };
              
              console.log('Loaded account structured summary:', {
                totalSites: context.account.totalSites,
                averagePerformance: context.account.averagePerformance,
                activeSites: accountSummary.overview?.activeSites
              });
            } else {
              // Fallback to basic account data if no AI summary exists
              console.warn('No AI summary found for account, using basic data');
              context.account = {
                id: currentAccountId,
                name: accountData?.name,
                accountType: accountData?.accountType,
                description: accountData?.description,
                status: accountData?.status,
                aiSummary: "AI summary not yet generated for this collection.",
                needsSummaryGeneration: true
              };
            }
          } catch (error) {
            console.error('Error fetching account AI summary:', error);
            context.account = {
              id: currentAccountId,
              name: accountData?.name,
              accountType: accountData?.accountType,
              error: "Could not load AI summary"
            };
          }
          
          // Add organization context
          context.organization = {
            id: currentOrg?.id,
            name: currentOrg?.name,
            accountType: currentOrg?.accountType,
            description: currentOrg?.description,
            totalAccounts: currentOrg?.accounts?.length || 0
          };
          break;
          
        case 'site':
          // Fetch pre-computed site AI summary
          const siteAccountId = accountId || currentAccount?.id;
          const currentSiteId = siteId;
          
          if (!siteAccountId || !currentSiteId) {
            console.warn('Missing account ID or site ID for site summary');
            break;
          }
          
          console.log('Fetching site structured summary for:', siteAccountId, currentSiteId);
          
          try {
            const siteSummaryDoc = await getDoc(doc(Firestore, `accounts/${siteAccountId}/sites/${currentSiteId}/structuredSummary/current`));
            
            if (siteSummaryDoc.exists()) {
              const siteSummary = siteSummaryDoc.data();
              
              context.site = {
                id: currentSiteId,
                siteName: contextData?.siteName,
                description: contextData?.description,
                url: contextData?.url,
                siteType: contextData?.siteType,
                status: contextData?.status,
                
                // Use structured summary data
                overview: siteSummary.overview,
                urls: siteSummary.urls,
                performance: siteSummary.performance,
                recommendations: siteSummary.recommendations,
                technologies: siteSummary.technologies,
                
                // Legacy compatibility
                performanceScore: siteSummary.overview?.performance,
                lastAnalyzed: siteSummary.lastAnalyzed,
                
                // Include original data for context
                productionDomains: contextData?.productionDomains || [],
                devUrl: contextData?.devUrl,
                testUrl: contextData?.testUrl,
                liveUrl: contextData?.liveUrl
              };
              
              console.log('Loaded site structured summary:', {
                performanceScore: context.site.performanceScore,
                recommendationsCount: context.site.recommendations?.length,
                hasPerformanceData: !!siteSummary.performance?.hasData
              });
            } else {
              // Fallback to basic site data if no AI summary exists
              console.warn('No AI summary found for site, using basic data');
              context.site = {
                id: currentSiteId,
                siteName: contextData?.siteName,
                description: contextData?.description,
                url: contextData?.url,
                siteType: contextData?.siteType,
                status: contextData?.status,
                productionDomains: contextData?.productionDomains || [],
                devUrl: contextData?.devUrl,
                testUrl: contextData?.testUrl,
                liveUrl: contextData?.liveUrl,
                aiSummary: "AI summary not yet generated for this site.",
                needsSummaryGeneration: true
              };
            }
          } catch (error) {
            console.error('Error fetching site AI summary:', error);
            context.site = {
              id: currentSiteId,
              siteName: contextData?.siteName,
              url: contextData?.url,
              error: "Could not load AI summary"
            };
          }
          
          // Add account and organization context (basic data)
          context.account = {
            id: currentAccount?.id,
            name: currentAccount?.name,
            accountType: currentAccount?.accountType,
            description: currentAccount?.description
          };
          
          context.organization = {
            id: currentOrg?.id,
            name: currentOrg?.name,
            accountType: currentOrg?.accountType,
            description: currentOrg?.description
          };
          break;
          
        default:
          // Handle any additional context types
          break;
      }
    } catch (error) {
      console.error('Error preparing context data:', error);
      // Fallback to basic context if detailed fetch fails
    }

    return context;
  }, [contextType, contextData, accountId, siteId, currentAccount, currentOrg]);

  // Cache context data to reduce database calls
  const getCachedContext = useCallback(async () => {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    
    // Return cached context if less than 1 hour old
    if (cachedContext && contextCacheTime && (now - contextCacheTime) < oneHour) {
      console.log('📋 Using cached context data');
      return cachedContext;
    }
    
    console.log('📋 Preparing fresh context data...');
    const freshContext = await prepareContextData();
    setCachedContext(freshContext);
    setContextCacheTime(now);
    return freshContext;
  }, [cachedContext, contextCacheTime, prepareContextData]);


  // Handle sending a new question
  const handleSendQuestion = async () => {
    if ((!newQuestion.trim() && !selectedFile) || isLoading) return;

    setIsLoading(true);
    setIsUploading(true);
    setError('');

    try {
      console.log('🚀 Starting AI Assistant request...');
      const startTime = Date.now();
      
      console.log('📋 Getting context data...');
      const contextData = await getCachedContext();
      console.log(`✅ Context data retrieved in ${Date.now() - startTime}ms`);
      
      let fileData = null;
      let fileContent = '';
      
      // Handle file upload if present
      if (selectedFile) {
        fileData = await uploadFile(selectedFile);
        fileContent = await readFileContent(selectedFile);
      }
      
      // Prepare the question with file content
      let fullQuestion = newQuestion.trim();
      if (fileContent) {
        fullQuestion += `\n\n[Attached file: ${selectedFile.name}]\n${fileContent}`;
      }
      
      // Call the Cloud Function to get AI response
      console.log('🤖 Calling OpenAI Assistant...');
      const aiStartTime = Date.now();
      const getAIResponse = httpsCallable(CloudFunctions, 'kwallCloud-getAIResponse');
      const result = await getAIResponse({
        question: fullQuestion,
        context: contextData,
        fileData: fileData
      });
      console.log(`✅ AI response received in ${Date.now() - aiStartTime}ms`);

      // Save the conversation to Firestore
      const collectionPath = getCollectionPath();
      const conversationsRef = collection(Firestore, collectionPath);
      
      const newConversation = {
        question: newQuestion.trim(),
        answer: result.data.answer,
        userId: authUser.user.uid,
        userName: authUser.user.displayName || authUser.user.email,
        contextType,
        contextId: contextData?.id || 'unknown',
        fileData: fileData,
        createdAt: serverTimestamp()
      };
      
      await addDoc(conversationsRef, newConversation);
      
      // Reload conversations based on current tab after sending
      if (tabValue === 0) {
        // Chat tab - reload latest conversation
        loadLatestConversation();
      }

      setNewQuestion('');
      setSelectedFile(null);
    } catch (error) {
      console.error('Error sending question:', error);
      setError('Failed to get AI response. Please try again.');
    }

    setIsLoading(false);
    setIsUploading(false);
  };

  // Handle deleting a conversation
  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;

    try {
      const collectionPath = getCollectionPath();
      const conversationRef = doc(Firestore, collectionPath, conversationToDelete.id);
      await deleteDoc(conversationRef);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setError('Failed to delete conversation');
    }

    setDeleteDialogOpen(false);
    setConversationToDelete(null);
  };

  // Handle Enter key press in the text field
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendQuestion();
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError(''); // Clear any errors when switching tabs
  };

  // Handle conversation expansion
  const toggleConversationExpansion = (conversationId) => {
    const newExpanded = new Set(expandedConversations);
    if (newExpanded.has(conversationId)) {
      newExpanded.delete(conversationId);
    } else {
      newExpanded.add(conversationId);
    }
    setExpandedConversations(newExpanded);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      // Check file type (text files, images, PDFs)
      const allowedTypes = [
        'text/plain',
        'text/csv',
        'application/json',
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/gif',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setError('Unsupported file type. Please upload text, image, PDF, or Office documents.');
        return;
      }
      
      setSelectedFile(file);
      setError('');
    }
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  // Upload file to Firebase Storage
  const uploadFile = async (file) => {
    const fileName = `ai-assistant/${Date.now()}-${file.name}`;
    const storageRef = ref(FirebaseStorage, fileName);
    
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      url: downloadURL,
      path: fileName
    };
  };

  // Read file content for AI processing
  const readFileContent = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const content = e.target.result;
        
        if (file.type.startsWith('image/')) {
          // For images, just return metadata
          resolve(`[Image: ${file.name}, Size: ${(file.size / 1024).toFixed(1)}KB]`);
        } else {
          // For text files, return actual content (truncated if too long)
          const textContent = typeof content === 'string' ? content : '';
          const truncatedContent = textContent.length > 5000 
            ? textContent.substring(0, 5000) + '...[truncated]'
            : textContent;
          resolve(truncatedContent);
        }
      };
      
      reader.onerror = reject;
      
      if (file.type.startsWith('image/')) {
        reader.readAsDataURL(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  // Render file attachment
  const renderFileAttachment = (fileData) => {
    if (!fileData) return null;
    
    return (
      <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachFileIcon fontSize="small" color="primary" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {fileData.name}
          </Typography>
          <Chip 
            label={`${(fileData.size / 1024).toFixed(1)}KB`} 
            size="small" 
            variant="outlined"
          />
        </Box>
        {fileData.url && (
          <Button 
            size="small" 
            href={fileData.url} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ mt: 1 }}
          >
            Download File
          </Button>
        )}
      </Box>
    );
  };

  return (
    <>
      <CardWithHeader
        title={title}
        headerIcon={<PsychologyIcon />}
        subtitle={`Get AI insights about your ${contextType}`}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="ai assistant tabs">
            <Tab 
              icon={<ChatIcon />} 
              iconPosition="start" 
              label="Chat" 
              sx={{ 
                textTransform: 'none',
                display: 'flex',
                flexDirection: 'row',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
            <Tab 
              icon={<HistoryIcon />} 
              iconPosition="start" 
              label="History" 
              sx={{ 
                textTransform: 'none',
                display: 'flex',
                flexDirection: 'row',
                '& .MuiSvgIcon-root': { mr: 1 }
              }}
            />
          </Tabs>
        </Box>

        {/* Chat Tab */}
        {tabValue === 0 && (
          <>
            {/* Latest Conversation Only */}
            {conversations.length > 0 && (
              <Box
                sx={{
                  maxHeight: maxHeight - 200,
                  overflowY: 'auto',
                  mb: 2,
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                  backgroundColor: '#fafafa'
                }}
              >
                <List sx={{ p: 1 }}>
                  {conversations.map((conversation, index) => (
                    <Box key={conversation.id}>
                      {/* User Question */}
                      <ListItem sx={{ alignItems: 'flex-start', px: 2 }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          <PersonIcon />
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {conversation.userName}
                              </Typography>
                              <Chip 
                                label="Question" 
                                size="small" 
                                sx={{ ml: 1, bgcolor: 'primary.main', color: 'white' }}
                              />
                            </Box>
                          }
                          secondary={
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', mt: 1, border: '1px solid rgba(0, 0, 0, 0.1)' }}>
                              <Typography variant="body2">
                                {conversation.question}
                              </Typography>
                              {renderFileAttachment(conversation.fileData)}
                            </Paper>
                          }
                        />
                      </ListItem>

                      {/* AI Response */}
                      <ListItem sx={{ alignItems: 'flex-start', px: 2, pb: 3 }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                          <AIIcon />
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                AI Assistant
                              </Typography>
                              <Chip 
                                label="Response" 
                                size="small" 
                                sx={{ ml: 1, bgcolor: 'secondary.main', color: 'white' }}
                              />
                            </Box>
                          }
                          secondary={
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', mt: 1, border: '1px solid rgba(0, 0, 0, 0.1)' }}>
                              {formatAIResponse(conversation.answer)}
                            </Paper>
                          }
                        />
                      </ListItem>

                      {/* No divider needed for single conversation */}
                    </Box>
                  ))}
                </List>
              </Box>
            )}

            {/* Selected File Display */}
            {selectedFile && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid rgba(0, 0, 0, 0.1)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFileIcon fontSize="small" color="primary" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedFile.name}
                    </Typography>
                    <Chip 
                      label={`${(selectedFile.size / 1024).toFixed(1)}KB`} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                  <IconButton size="small" onClick={handleRemoveFile}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Input Area */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                placeholder="Ask a question about your data..."
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                size="small"
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <input
                  accept=".txt,.csv,.json,.pdf,.jpg,.jpeg,.png,.gif,.docx,.xlsx"
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="file-upload">
                  <Tooltip title="Attach file (max 10MB)">
                    <IconButton 
                      color="primary" 
                      aria-label="upload file" 
                      component="span"
                      disabled={isLoading}
                      size="small"
                    >
                      <AttachFileIcon />
                    </IconButton>
                  </Tooltip>
                </label>
                <Button
                  variant="contained"
                  onClick={handleSendQuestion}
                  disabled={(!newQuestion.trim() && !selectedFile) || isLoading}
                  startIcon={isLoading ? <CircularProgress size={16} /> : <SendIcon />}
                  sx={{ minWidth: 100 }}
                  size="small"
                >
                  {isLoading ? (isUploading ? 'Uploading...' : 'Sending...') : 'Send'}
                </Button>
              </Box>
            </Box>
          </>
        )}

        {/* History Tab */}
        {tabValue === 1 && (
          <Box
            sx={{
              maxHeight,
              overflowY: 'auto',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
              backgroundColor: '#fafafa'
            }}
          >
            {conversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No conversation history yet. Start a conversation in the Chat tab!
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                {conversations.slice().reverse().map((conversation, index) => {
                  const isExpanded = expandedConversations.has(conversation.id);
                  const conversationDate = conversation.createdAt 
                    ? new Date(conversation.createdAt.seconds * 1000)
                    : new Date();
                  
                  return (
                    <Paper 
                      key={conversation.id} 
                      sx={{ 
                        mb: 2, 
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                    >
                      {/* Collapsible Header */}
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'grey.50',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          '&:hover': {
                            bgcolor: 'grey.100'
                          }
                        }}
                        onClick={() => toggleConversationExpansion(conversation.id)}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {conversation.question.length > 60 
                              ? `${conversation.question.substring(0, 60)}...` 
                              : conversation.question}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {conversationDate.toLocaleDateString()} at {conversationDate.toLocaleTimeString()}
                            {conversation.fileData && (
                              <Chip 
                                label="📎 File" 
                                size="small" 
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {canDeleteConversation(conversation) && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConversationToDelete(conversation);
                                setDeleteDialogOpen(true);
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small">
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Expandable Content */}
                      {isExpanded && (
                        <Box sx={{ p: 2 }}>
                          {/* User Question */}
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                                <PersonIcon fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {conversation.userName}
                              </Typography>
                              <Chip 
                                label="Question" 
                                size="small" 
                                sx={{ ml: 1, bgcolor: 'primary.main', color: 'white', height: 20 }}
                              />
                            </Box>
                            
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.light', border: '1px solid rgba(0, 0, 0, 0.1)' }}>
                              <Typography variant="body2" sx={{ color: 'primary.contrastText' }}>
                                {conversation.question}
                              </Typography>
                              {conversation.fileData && (
                                <Box sx={{ mt: 2 }}>
                                  {renderFileAttachment(conversation.fileData)}
                                  {conversation.fileData.url && (
                                    <Button
                                      size="small"
                                      startIcon={<GetAppIcon />}
                                      href={conversation.fileData.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ mt: 1 }}
                                    >
                                      Download File
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </Paper>
                          </Box>

                          {/* AI Response */}
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'secondary.main' }}>
                                <AIIcon fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                AI Assistant
                              </Typography>
                              <Chip 
                                label="Response" 
                                size="small" 
                                sx={{ ml: 1, bgcolor: 'secondary.main', color: 'white', height: 20 }}
                              />
                            </Box>
                            
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'secondary.light', border: '1px solid rgba(0, 0, 0, 0.1)' }}>
                              <Box sx={{ color: 'secondary.contrastText' }}>
                                {formatAIResponse(conversation.answer)}
                              </Box>
                            </Paper>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </CardWithHeader>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this conversation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConversation} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIAssistant;